import { useState, useEffect } from "react";
import {
  Heart,
  Share2,
  Phone,
  MessageCircle,
  Calendar,
  Images,
  Download,
  Baby,
  Dice5,
  Building2,
  Trees,
  Film,
  Droplet,
  Sun,
  ScanLine,
  Flame,
  Dumbbell,
  Droplets,
  Car,
  Warehouse,
  Landmark,
  Lightbulb,
  Gamepad2,
  Bike,
  CheckCircle,
  CloudRain,
  ShieldCheck,
  BatteryCharging,
  Check,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import apiClient from "../common/apiClient";
import { toast } from "react-hot-toast";
import ShareModal from "./ShareModal";


const AMENITIES_LIST = [
  { id: "Children's Play Area", icon: Baby },
  { id: "Board Games", icon: Dice5 },
  { id: "Lift(s)", icon: Building2 },
  { id: "Landscaping & Trees", icon: Trees },
  { id: "Mini Theatre", icon: Film },
  { id: "RO Water System", icon: Droplet },
  { id: "Solar Power System", icon: Sun },
  { id: "Sensor Operated Lights", icon: ScanLine },
  { id: "Fire Fighting System", icon: Flame },
  { id: "Gymnasium", icon: Dumbbell },
  { id: "24x7 Water Supply", icon: Droplets },
  { id: "Car Parking", icon: Car },
  { id: "Multipurpose Hall", icon: Warehouse },
  { id: "Community Hall", icon: Landmark },
  { id: "Street Lighting", icon: Lightbulb },
  { id: "Indoor Games", icon: Gamepad2 },
  { id: "Cycling & Jogging Track", icon: Bike },
  { id: "Internal Roads & Footpaths", icon: CheckCircle },
  { id: "Rain Water Harvesting", icon: CloudRain },
  { id: "24x7 Security", icon: ShieldCheck },
  { id: "Power Backup", icon: BatteryCharging },
];


const PropertyPage = () => {
  const [isSaved, setIsSaved] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  // --- Contact state for View Contact ---
  const [contact, setContact] = useState(null); // { name, number }
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState("");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [currentShareText, setCurrentShareText] = useState("");
  const userId = Cookies.get("userId");

  // --- Get Contact API based on data from NewBuilderCard ---
  const handleGetContact = async () => {
    if (!builderData) return;

    // NOTE: replace with your real auth token / cookie logic
    const AUTH_TOKEN = Cookies.get("token");

    const payload = {
      listedBy: builderData?.listedBy || "builders",
      propId: builderData?._id || builderData?._id,
      userId: userId, // TODO: replace with logged-in user's id
    };

    try {
      setContactLoading(true);
      setContactError("");

      const response = await apiClient(
        "post",
        "/cjidnvij/ceksfbuebijn/user/broker-properties/contact",
        payload,
        {
          headers: {
            Authorization: `Bearer ${AUTH_TOKEN}`,
          },
        }
      );

      if (!response.success) {
        throw new Error(response.error || "Failed to fetch contact");
      }

      const data = response.data;

      // normalize various shapes
      const name =
        data?.data?.name || data?.name || data?.contactName || "Contact";
      const number = (
        data?.data?.number ||
        data?.number ||
        data?.contactNumber ||
        ""
      )
        .toString()
        .replace(/\s+/g, "");

      setContact({ name, number });
    } catch (err) {
      console.error("Get contact error:", err);
      setContactError("Could not fetch contact. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  // Get builder data from location state
  const builderData = location.state?.builderData;

  // Default property data (fallback if no data is passed)
  const defaultPropertyData = {
    name: "Sahjanand S+",
    developer: "Sahjanand Group",
    location: "Vaval, Gandhinagar",
    priceRange: "₹1.05 Cr - ₹1.12 Cr",
    avgPrice: "₹28,000 / SqM",
    configuration: "4 BHK Flat",
    size: "334 SqYd - 361 SqYd",
    possessionStarts: "Mar, 2026",
    totalTowers: 1,
    totalFloors: 13,
    totalUnits: 60,
    projectArea: "3,065 SqYd",
    launchDate: "Jan, 2023",
    parking: "1 car allotted",
    status: "Under Construction",
    images: [
      "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg",
      "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg",
      "https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg",
      "https://images.pexels.com/photos/280222/pexels-photo-280222.jpeg",
      "https://images.pexels.com/photos/259588/pexels-photo-259588.jpeg",
    ],
    developerInfo: {
      name: "Sahjanand Group",
      role: "Developer",
      description:
        "Sahjanand Group is one leading Real Estate/builder & construction company situated in Gandhinagar. The company's empire building was envisioned in 2008 by Nirmesh Manubhai Patel. Redefining lifestyle and serving the customers the best accommodation, delivering uniqueness and excellence in our group. We also aim at building according to the demand of the customer though continuous innovations.",
    },
  };

  // Fallback image URL
  const fallbackImage = "/image/Image-not-available.png";

  // Function to handle image error
  const handleImageError = (e) => {
    e.target.src = fallbackImage;
  };

  const safeConfig = (bhk) => {
    if (Array.isArray(bhk)) return bhk.join(", ");
    if (bhk != null && bhk !== "") return `${bhk} BHK`;
    return null;
  };

  // Function to format price range with lac/cr suffixes
  const formatPriceRange = (min, max) => {
    if (!min || !max) return "Price on Request";

    const formatPrice = (price) => {
      if (price >= 10000000) {
        // 1 crore = 10,000,000
        const crValue = price / 10000000;
        return `₹${crValue % 1 === 0 ? Math.floor(crValue) : crValue.toFixed(2)
          }Cr`;
      } else if (price >= 100000) {
        // 1 lakh = 100,000
        const lacValue = price / 100000;
        return `₹${lacValue % 1 === 0 ? Math.floor(lacValue) : lacValue.toFixed(2)
          }Lac`;
      } else {
        return `₹${price.toLocaleString()}`;
      }
    };

    const formattedMin = formatPrice(min);
    const formattedMax = formatPrice(max);

    return `${formattedMin} - ${formattedMax}`;
  };

  // Function to format average price
  const formatAvgPrice = (min, max) => {
    if (!min || !max) return "";
    const avg = (min + max) / 2;

    if (avg >= 10000000) {
      const crValue = avg / 10000000;
      return `₹${crValue % 1 === 0 ? Math.floor(crValue) : crValue.toFixed(2)
        }Cr/SqFt`;
    } else if (avg >= 100000) {
      const lacValue = avg / 100000;
      return `₹${lacValue % 1 === 0 ? Math.floor(lacValue) : lacValue.toFixed(2)
        }Lac/SqFt`;
    } else {
      return `₹${Math.round(avg)}/SqFt`;
    }
  };

  // Process builder data if available
  const propertyData = builderData
    ? {
      name: builderData.title || "",
      reraId: builderData.reraId || null,
      developer: builderData.name || "Developer",
      location: `${builderData.area}, ${builderData.city}` || "",
      priceRange:
        builderData.priceRange?.min && builderData.priceRange?.max
          ? formatPriceRange(
            builderData.priceRange.min,
            builderData.priceRange.max
          )
          : "Price on Request",
      avgPrice:
        builderData.priceRange?.min && builderData.priceRange?.max
          ? formatAvgPrice(
            builderData.priceRange.min,
            builderData.priceRange.max
          )
          : "",

      configuration:
        safeConfig(builderData.bhk) || defaultPropertyData.configuration,
      size: `${builderData.sqFt} SqFt` || "",
      possessionStarts: builderData.PossessionDate
        ? new Date(builderData.PossessionDate).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        })
        : "Mar, 2026",
      totalTowers: builderData.totalTowers || "",
      totalFloors: builderData.floors || "",
      totalUnits: builderData.totalUnits || "",
      projectArea: `${builderData.sqFt} SqFt` || "",
      launchDate: builderData.listedDate
        ? new Date(builderData.listedDate).toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        })
        : "",
      parking: builderData.parking || "",
      status: builderData.age || builderData.propertyCurrentStatus || "",
      images: builderData.media?.map((item) => item.url.trim()) || "",
      amenities: builderData.amenities || [],
      developerInfo: {
        name: builderData.developerName || "",
        role: "Developer",
        description: builderData.description || "",
      },
    }
    : defaultPropertyData;

  // Ensure images array has valid URLs or fallback
  const processedImages =
    propertyData.images && propertyData.images.length > 0
      ? propertyData.images.map((img) => img || fallbackImage)
      : [fallbackImage];

  // Check if there are PDF files in the media
  const hasPDFMedia = builderData?.media?.some((item) =>
    item.url?.toLowerCase().endsWith(".pdf")
  );

  // Get the first PDF file
  const pdfFile = builderData?.media?.find((item) =>
    item.url?.toLowerCase().endsWith(".pdf")
  );

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "amenities", label: "Amenities" },
  ];

  // Function to handle voucher download
  const handleDownloadVoucher = () => {
    if (!pdfFile?.url) {
      toast.error("Brochure not uploaded by user");
      return;
    }

    const link = document.createElement("a");
    link.href = pdfFile.url;
    link.download =
      pdfFile.name || pdfFile.url.split("/").pop() || "property_voucher.pdf";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Image Gallery Component
  const ImageGallery = () => {
    const getDisplayImages = () => {
      const images = [...processedImages];
      while (images.length < 5) {
        images.push(fallbackImage);
      }
      return images.slice(0, 5);
    };

    const displayImages = getDisplayImages();
    const remainingCount = processedImages.length - 4;

    return (
      <>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
            {/* Large Image (Left Side) */}
            <div className="relative md:row-span-2 h-[250px] md:h-[400px] rounded-lg overflow-hidden group cursor-pointer">
              <img
                src={displayImages[0]}
                alt="Main property view"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onClick={() => setSelectedImageIndex(0)}
                onError={handleImageError}
              />
            </div>

            {/* Right-side grid (make total height same as left image) */}
            <div className="grid grid-cols-2 gap-2 md:h-[400px]">
              {displayImages.slice(1, 5).map((image, index) => (
                <div
                  key={index + 1}
                  className="relative h-[120px] md:h-[calc(400px/2-4px)] rounded-lg overflow-hidden group cursor-pointer"
                  onClick={() => setSelectedImageIndex(index + 1)}
                >
                  <img
                    src={image}
                    alt={`Property view ${index + 2}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={handleImageError}
                  />

                  {/* "+X more" overlay */}
                  {index === 3 && remainingCount > 0 && (
                    <div
                      className="absolute inset-0 bg-gray-900/70 flex items-center justify-center cursor-pointer hover:bg-gray-900/80 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAllPhotos(true);
                      }}
                    >
                      <div className="text-center">
                        <div className="bg-white text-gray-900 px-3 py-2 rounded-lg flex items-center space-x-1 font-semibold text-sm">
                          <Images className="w-4 h-4" />
                          <span>+{remainingCount}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Full Photo Gallery Modal */}
        {showAllPhotos && (
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
            <button
              onClick={() => setShowAllPhotos(false)}
              className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors z-10"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {processedImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Property view ${index + 1}`}
                    className="w-full h-48 md:h-64 object-cover rounded-lg"
                    onError={handleImageError}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Property Details Component
  const PropertyDetails = () => {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Tabs */}
        <div className="border-b overflow-x-auto">
          <div className="flex min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 md:px-6 py-3 md:py-4 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id
                    ? "text-gray-600 border-b-2 border-gray-600"
                    : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-4 md:p-6">
          {activeTab === "overview" && (
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
                <span className="mr-2">🏠</span> Overview
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-12 gap-y-4 md:gap-y-6">
                <div className="space-y-3 md:space-y-4">
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Price
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.priceRange}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Avg. Price
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.avgPrice}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Size (Area)
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.size}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Project Area
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.projectArea}
                    </span>
                  </div>
                </div>
                <div className="space-y-3 md:space-y-4">
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      No. of Floors
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.totalFloors}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Total Units
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.totalUnits}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Possession Starts
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.possessionStarts}
                    </span>
                  </div>
                </div>
                <div className="space-y-3 md:space-y-4 md:col-span-2">
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Launch Date
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base">
                      {propertyData.launchDate}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      Project Status
                    </span>
                    <span className="font-semibold text-sm md:text-base">
                      {propertyData.status}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 md:py-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm md:text-base">
                      RERA ID
                    </span>
                    <span className="font-semibold text-gray-900 text-sm md:text-base break-all text-right ml-2">
                      {propertyData.reraId}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === "amenities" && (
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
                <span className="mr-2">✨</span> Amenities
              </h3>
              {(() => {
                let amenitiesList = [];
                const rawAmenities = propertyData.amenities;

                if (rawAmenities && rawAmenities.length > 0) {
                  // Check if it's the double encoded JSON string case
                  if (
                    rawAmenities.length === 1 &&
                    typeof rawAmenities[0] === "string" &&
                    rawAmenities[0].startsWith("[")
                  ) {
                    try {
                      amenitiesList = JSON.parse(rawAmenities[0]);
                    } catch (e) {
                      amenitiesList = rawAmenities;
                    }
                  } else {
                    amenitiesList = rawAmenities;
                  }
                }

                if (amenitiesList.length === 0) {
                  return <p className="text-gray-500">No amenities listed.</p>;
                }

                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {amenitiesList.map((amenityName, index) => {
                      const amenityObj = AMENITIES_LIST.find(
                        (a) => a.id === amenityName
                      );
                      const Icon = amenityObj ? amenityObj.icon : Check;

                      return (
                        <div
                          key={index}
                          className="flex flex-col items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-gray-300 transition-all bg-white"
                        >
                          <Icon
                            size={26}
                            strokeWidth={1.5}
                            className="text-black"
                          />
                          <span className="text-sm text-center leading-tight text-gray-900">
                            {amenityName}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Developer Card Component
  const DeveloperCard = () => {
    return (
      <div className="bg-gradient-to-br rounded-lg shadow-sm p-4 md:p-6 border">
        {/* Developer Icon */}
        <div className="flex items-start space-x-3 mb-4">
          <div className="p-2 md:p-3 rounded-lg">
            <svg
              className="w-5 h-5 md:w-6 md:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-base md:text-lg font-bold text-gray-900">
              {propertyData.developerInfo.name}
            </h3>
            <p className="text-xs md:text-sm text-gray-600">
              {propertyData.developerInfo.role}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs md:text-sm text-gray-700 leading-relaxed mb-4 md:mb-6">
          {/* {propertyData.developerInfo.description} */}
        </p>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* View Contact button */}
          {!contact && (
            <button
              onClick={handleGetContact}
              disabled={contactLoading}
              className={`w-full bg-[#503691] text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-sm ${contactLoading ? "opacity-70" : "hover:bg-[#503691]"
                }`}
            >
              <Phone className="w-5 h-5" />
              <span>
                {contactLoading ? "Getting Contact…" : "View Contact"}
              </span>
            </button>
          )}

          {/* WhatsApp (enabled only after contact is available) */}
          <a
            href={
              contact?.number
                ? `https://wa.me/${contact.number.replace(/[^\d]/g, "")}`
                : undefined
            }
            target="_blank"
            rel="noreferrer"
            className={`w-full ${contact?.number
                ? "bg-white hover:bg-gray-50 cursor-pointer"
                : "bg-white opacity-60 cursor-not-allowed"
              } text-gray-900 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 border border-gray-200`}
            onClick={(e) => {
              if (!contact?.number) e.preventDefault();
            }}
          >
            <MessageCircle className="w-5 h-5 text-green-600" />
            <span>WhatsApp</span>
          </a>
          {/* Download Voucher button */}
          <button
            onClick={handleDownloadVoucher}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 border border-gray-200"
          >
            <Download className="w-5 h-5" />
            <span>Download Brochure</span>
          </button>
        </div>

        {/* Error, if any */}
        {contactError && (
          <div className="mt-3 px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
            {contactError}
          </div>
        )}

        {/* Contact chip (shown after success) - Updated to match second screenshot design */}
        {contact && (
          <div className="flex items-center justify-between py-2 px-4 rounded-lg bg-white text-[#503691] border border-gray-200 mt-3">
            {/* Contact Name and Number */}
            <p className="font-medium">
              {contact.name} - {contact.number || "NA"}
            </p>

            {/* Icons for Phone and WhatsApp */}
            <div className="flex items-center gap-3">
              {/* Phone Link */}
              {contact.number && (
                <a
                  href={`tel:${contact.number}`}
                  className="text-[#503691] hover:text-blue-600 transition-colors"
                  title="Call"
                >
                  <Phone className="h-6 w-6" />
                </a>
              )}

              {/* WhatsApp Link */}
              {contact.number && (
                <a
                  href={`https://wa.me/${contact.number.replace(/[^\d]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 transition-transform"
                  title="WhatsApp"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    className="h-6 w-6"
                    fill="#25D366"
                  >
                    <path d="M92.1 254.6c0 24.9 7 49.2 20.2 70.1l3.1 5-13.3 48.6L152 365.2l4.8 2.9c20.2 12 43.4 18.4 67.1 18.4h.1c72.6 0 133.3-59.1 133.3-131.8c0-35.2-15.2-68.3-40.1-93.2c-25-25-58-38.7-93.2-38.7c-72.7 0-131.8 59.1-131.9 131.8zM274.8 330c-12.6 1.9-22.4 .9-47.5-9.9c-36.8-15.9-61.8-51.5-66.9-58.7c-.4-.6-.7-.9-.8-1.1c-2-2.6-16.2-21.5-16.2-41c0-18.4 9-27.9 13.2-32.3c.3-.3 .5-.5 .7-.8c3.6-4 7.9-5 10.6-5c2.6 0 5.3 0 7.6 .1c.3 0 .5 0 .8 0c2.3 0 5.2 0 8.1 6.8c1.2 2.9 3 7.3 4.9 11.8c3.3 8 6.7 16.3 7.3 17.6c1 2 1.7 4.3 .3 6.9c-3.4 6.8-6.9 10.4-9.3 13c-3.1 3.2-4.5 4.7-2.3 8.6c15.3 26.3 30.6 35.4 53.9 47.1c4 2 6.3 1.7 8.6-1c2.3-2.6 9.9-11.6 12.5-15.5c2.6-4 5.3-3.3 8.9-2s23.1 10.9 27.1 12.9c.8 .4 1.5 .7 2.1 1c2.8 1.4 4.7 2.3 5.5 3.6c.9 1.9 .9 9.9-2.4 19.1c-3.3 9.3-19.1 17.7-26.7 18.8zM448 96c0-35.3-28.7-64-64-64H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96zM148.1 393.9L64 416l22.5-82.2c-13.9-24-21.2-51.3-21.2-79.3C65.4 167.1 136.5 96 223.9 96c42.4 0 82.2 16.5 112.2 46.5c29.9 30 47.9 69.8 47.9 112.2c0 87.4-72.7 158.5-160.1 158.5c-26.6 0-52.7-6.7-75.8-19.3z" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Daily Contact Count */}
        {builderData?.dailyContact !== undefined && (
          <p className="text-xs text-gray-500 mt-3 text-center">
            {builderData.dailyContact} people contacted this property today
          </p>
        )}
      </div>
    );
  };

  const handleShareClick = () => {
    const shareText = `
      **${propertyData.name || "Property Available"}**

      ${propertyData.developer ? `Developer: ${propertyData.developer}` : ""}
      ${propertyData.location ? `Location: ${propertyData.location}` : ""}
      ${propertyData.priceRange ? `Price: ${propertyData.priceRange}` : ""}
      ${propertyData.configuration ? `Configuration: ${propertyData.configuration}` : ""}
      ${propertyData.size ? `Size: ${propertyData.size}` : ""}
      ${propertyData.possessionStarts ? `Possession: ${propertyData.possessionStarts}` : ""}
      ${propertyData.status ? `Status: ${propertyData.status}` : ""}

      **Contact:**
      Name: ${contact?.name || "NA"}
      Number: ${contact?.number || "NA"}
      `.trim();

    setCurrentShareText(shareText);
    setIsShareModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        {/* Header content can go here */}
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 md:py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 flex items-center">
            <button
              onClick={() => window.history.back()}
              className="flex items-center hover:text-gray-900 cursor-pointer font-semibold transition-colors text-sm md:text-base"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
          </div>

          <div className="flex items-center space-x-2 md:space-x-4">
            <button
              onClick={() => setIsSaved(!isSaved)}
              className={`flex items-center space-x-1 md:space-x-2 px-3 py-2 md:px-4 md:py-2 rounded-lg transition-colors ${isSaved
                  ? "text-red-500 bg-red-50"
                  : "text-gray-700 hover:bg-gray-100"
                }`}
            >
              <Heart
                className={`w-4 h-4 md:w-5 md:h-5 ${isSaved ? "fill-current" : ""
                  }`}
              />
              <span className="hidden sm:inline text-sm md:text-base">
                Save
              </span>
            </button>
            <button
              onClick={handleShareClick}
              className="flex items-center space-x-1 md:space-x-2 px-3 py-2 md:px-4 md:py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Share2 className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline text-sm md:text-base">
                Share
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 pb-8 md:pb-12">
        {/* Image Gallery - Full Width */}
        <div className="mb-4 md:mb-6">
          <ImageGallery />
        </div>

        {/* Property Title and Price - Full Width */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          {/* Property Name and Download Button - ALWAYS IN SAME LINE */}
          <div className="flex flex-nowrap justify-between items-start gap-3 mb-2">
            <h1 className="text-xl md:text-3xl font-bold text-gray-900 break-words flex-1 min-w-0">
              {propertyData.name}
            </h1>

            {/* Show Download Voucher button only if PDF exists */}
            {/* {hasPDFMedia && (
              <button
                onClick={handleDownloadVoucher}
                className="flex items-center space-x-2 bg-[#503691] text-white font-semibold py-2 px-3 md:px-4 rounded-lg transition-colors shadow-sm flex-shrink-0 text-sm md:text-base whitespace-nowrap"
              >
                <Download className="w-4 h-4" />
                <span>Download Voucher</span>
              </button>
            )} */}
          </div>

          <p className="text-gray-600 mb-1 text-sm md:text-base">
            by {propertyData.developer}
          </p>
          <p className="text-gray-500 flex items-center mb-3 md:mb-4 text-sm md:text-base">
            <span className="mr-1">📍</span> {propertyData.location}
          </p>
          <div className="flex items-baseline space-x-2 mb-4 md:mb-6">
            <span className="text-xl md:text-3xl font-bold text-gray-900">
              {propertyData.priceRange}
            </span>
            <span className="text-xs md:text-sm text-gray-500">onwards</span>
          </div>

          {/* Quick Info Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            <div className="rounded-lg p-3 md:p-4">
              <p className="text-xs text-gray-500 mb-1">Avg. Price</p>
              <p className="text-xs md:text-sm font-semibold text-gray-900 break-words">
                {propertyData.avgPrice}
              </p>
            </div>
            <div className="rounded-lg p-3 md:p-4">
              <p className="text-xs text-gray-500 mb-1">Configuration</p>
              <p className="text-xs md:text-sm font-semibold text-gray-900 break-words">
                {propertyData.configuration}
              </p>
            </div>
            <div className="rounded-lg p-3 md:p-4">
              <p className="text-xs text-gray-500 mb-1">Size</p>
              <p className="text-xs md:text-sm font-semibold text-gray-900 break-words">
                {propertyData.size}
              </p>
            </div>
            <div className="rounded-lg p-3 md:p-4">
              <p className="text-xs text-gray-500 mb-1">Possession Starts</p>
              <p className="text-xs md:text-sm font-semibold text-gray-900 break-words">
                {propertyData.possessionStarts}
              </p>
            </div>
          </div>
        </div>

        {/* Two Column Layout - Details and Developer Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Left Column - Property Details */}
          <div className="lg:col-span-2">
            <PropertyDetails />
          </div>

          {/* Right Column - Developer Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 md:top-24">
              <DeveloperCard />
            </div>
          </div>
        </div>
      </div>
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shareText={currentShareText}
      />
    </div>
  );
};

export default PropertyPage;
